<template>
  <div class="pay-fees">
    <div class="pay-fees__card">
      <div class="pay-fees__header">
        <TenantLogo class="pay-fees__logo" />
        <h1>{{ clubName }}</h1>
        <p class="muted">Season fees</p>
      </div>

      <!-- Step 1: look the member up by the family's email -->
      <template v-if="!status">
        <p class="muted">
          Enter the email address the club holds for you and we'll find your
          player's season fees.
        </p>
        <form class="lookup-form" @submit.prevent="lookup">
          <input
            v-model="email"
            type="email"
            required
            placeholder="you@example.com"
            autocomplete="email"
          />
          <button type="submit" :disabled="busy || !email">
            {{ busy ? "Looking up…" : "Find my fees" }}
          </button>
        </form>
        <p v-if="error" class="error-text">{{ error }}</p>
      </template>

      <!-- Step 2: the ledger + pay buttons -->
      <template v-else-if="status.has_account">
        <h2>{{ status.member_name }}</h2>
        <p class="muted">{{ status.plan_name }}</p>

        <div class="summary" :class="{ cleared: status.ledger.cleared }">
          <template v-if="status.ledger.cleared">
            All paid — nothing owing. Thank you!
          </template>
          <template v-else>
            Outstanding: <strong>{{ money(status.ledger.outstanding) }}</strong>
            <span v-if="status.ledger.discount > 0" class="muted">
              (includes −{{ money(status.ledger.discount) }} voucher)
            </span>
          </template>
        </div>

        <table class="stages">
          <tbody>
            <tr v-for="inst in status.ledger.installments" :key="inst.id">
              <td>
                <strong>{{ inst.label }}</strong>
                <span v-if="inst.due_date" class="muted due">
                  due {{ formatDate(inst.due_date) }}
                </span>
              </td>
              <td class="amount">
                <template v-if="inst.outstanding_pence === 0">
                  <span class="paid-pill">Paid</span>
                </template>
                <template v-else>
                  {{ money(inst.outstanding_pence) }}
                </template>
              </td>
              <td class="action">
                <button
                  v-if="inst.outstanding_pence > 0"
                  :disabled="busy"
                  @click="pay(inst)"
                >
                  {{ busy ? "…" : "Pay now" }}
                </button>
              </td>
            </tr>
          </tbody>
        </table>

        <p class="muted small">
          Payments are processed securely by Stripe. If you've arranged
          payment with the club already, no action is needed.
        </p>
        <p v-if="error" class="error-text">{{ error }}</p>
        <button class="link-button" @click="reset">Look up a different email</button>
      </template>

      <!-- Known email, but no season-fee account -->
      <template v-else>
        <p>
          We found your details but there are no season fees set up yet —
          please check with the club.
        </p>
        <button class="link-button" @click="reset">Try a different email</button>
      </template>
    </div>
  </div>
</template>

<script>
import { StripeConnect } from "@/services/stripe-connect-service.js";
import {
  getClubId,
  getTenantName,
  getPaymentsCurrency,
} from "@/config/tenant";
import TenantLogo from "@/components/TenantLogo/index.vue";

export default {
  name: "PayFees",
  components: { TenantLogo },
  data() {
    return {
      email: "",
      status: null,
      busy: false,
      error: "",
    };
  },
  computed: {
    clubName() {
      return getTenantName();
    },
  },
  methods: {
    money(amountPence) {
      return new Intl.NumberFormat("en-AU", {
        style: "currency",
        currency: getPaymentsCurrency(),
      }).format((amountPence || 0) / 100);
    },
    formatDate(iso) {
      return new Date(iso).toLocaleDateString("en-AU", {
        day: "numeric",
        month: "short",
        year: "numeric",
      });
    },
    async lookup() {
      this.busy = true;
      this.error = "";
      try {
        const response = await StripeConnect.getPublicInstallmentStatus(
          getClubId(),
          this.email.trim(),
        );
        this.status = response.data.data;
      } catch (error) {
        this.error =
          error?.response?.data?.error ||
          "Couldn't look that up — please try again.";
      } finally {
        this.busy = false;
      }
    },
    async pay(inst) {
      this.busy = true;
      this.error = "";
      try {
        const response = await StripeConnect.createInstallmentCheckoutByEmail(
          getClubId(),
          this.email.trim(),
          this.status.plan_id,
          inst.id,
        );
        window.location.href = response.data.data.url;
      } catch (error) {
        this.error =
          error?.response?.data?.error || "Couldn't start the payment.";
        this.busy = false;
      }
    },
    reset() {
      this.status = null;
      this.email = "";
      this.error = "";
    },
  },
};
</script>

<style scoped lang="scss">
.pay-fees {
  min-height: 100vh;
  background: #f5f6f8;
  display: flex;
  justify-content: center;
  padding: 40px 16px;

  &__card {
    background: #fff;
    border-radius: 16px;
    border: 1px solid #e9ecef;
    box-shadow: 0 4px 18px rgba(0, 0, 0, 0.06);
    padding: 32px;
    width: 100%;
    max-width: 480px;
    height: fit-content;
  }

  &__header {
    text-align: center;
    margin-bottom: 20px;

    h1 {
      font-size: 1.3rem;
      margin: 10px 0 2px;
    }
  }

  &__logo {
    width: 64px;
    height: 64px;
    margin: 0 auto;
  }

  h2 {
    font-size: 1.1rem;
    margin-bottom: 2px;
  }

  .muted {
    color: #6c757d;
    font-size: 0.9rem;

    &.small {
      font-size: 0.78rem;
    }
  }

  .lookup-form {
    display: flex;
    gap: 8px;
    margin-top: 14px;

    input {
      flex: 1;
      padding: 10px 12px;
      border: 1px solid #ced4da;
      border-radius: 8px;
    }

    button {
      padding: 10px 16px;
      border: none;
      border-radius: 8px;
      background: #198754;
      color: #fff;
      font-weight: 600;
      cursor: pointer;

      &:disabled {
        opacity: 0.6;
      }
    }
  }

  .summary {
    margin: 14px 0;
    padding: 12px 14px;
    border-radius: 10px;
    background: #fff8e1;
    border: 1px solid #ffe08a;
    font-size: 0.95rem;

    &.cleared {
      background: #d1e7dd;
      border-color: #a3cfbb;
    }
  }

  .stages {
    width: 100%;
    border-collapse: collapse;

    td {
      padding: 10px 6px;
      border-bottom: 1px solid #f1f3f5;
      vertical-align: middle;
    }

    .due {
      display: block;
      font-size: 0.78rem;
    }

    .amount {
      text-align: right;
      font-weight: 700;
      white-space: nowrap;
    }

    .action {
      text-align: right;
      width: 96px;

      button {
        padding: 7px 14px;
        border: none;
        border-radius: 8px;
        background: #198754;
        color: #fff;
        font-weight: 600;
        cursor: pointer;

        &:disabled {
          opacity: 0.6;
        }
      }
    }

    .paid-pill {
      background: #d1e7dd;
      color: #0f5132;
      border-radius: 999px;
      padding: 3px 12px;
      font-size: 0.78rem;
      font-weight: 700;
    }
  }

  .error-text {
    color: #dc3545;
    font-size: 0.88rem;
    margin-top: 10px;
  }

  .link-button {
    margin-top: 14px;
    border: none;
    background: none;
    color: #6c757d;
    text-decoration: underline;
    cursor: pointer;
    font-size: 0.83rem;
    padding: 0;
  }
}
</style>
